import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './InkThread.css';

// A scroll gimmick for the "how it works" page: one line of ink winding down
// the page behind everything, drawn as far as you have scrolled, with a
// hand-drawn arrowhead at its tip. The line keeps to one margin and, in each
// gap between the parts of the page (.hw-gap), loops across to the other -
// until the last gap, where it swings in and stops, pointing down at "on the
// day" (OnTheDay.js), which has a scroll scene of its own.
//
// Pure decoration: behind the content, no clicks, aria-hidden. Visitors who
// asked for less motion get the line drawn in full, standing still.

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function buildPath(w, h, gaps) {
  // In the margin: halfway into the gutter beside the page's content
  // (pages/HowItWorks.css: 1080 wide, --gutter at the sides).
  const gutter = Math.min(56, Math.max(20, w * 0.04));
  const m = Math.max(8, (Math.max(0, w - 1080) / 2 + gutter) * 0.45);
  const sides = [w - m, m];
  let side = 0;
  let x = sides[side];
  let y = 220;
  let d = `M${x - 40} 92 C${x - 10} 90, ${x} 120, ${x} ${y}`;

  // Across at every gap but the last; at the last one - the start of the
  // day itself - swing in to the middle and stop, pointing down at it.
  const crossings = gaps.slice(0, -1);
  const last = gaps[gaps.length - 1] ?? h - 70;

  crossings.forEach((g) => {
    const to = sides[1 - side];
    const dir = to > x ? 1 : -1;
    const top = g - 150;
    // Down the margin, wavering a little towards the page.
    if (top > y + 20) {
      d += ` C${x} ${y + (top - y) / 3}, ${x + dir * 18} ${y + ((top - y) * 2) / 3}, ${x} ${top}`;
    }
    // Swing in, one loop of the pen, and on to the other side.
    const lx = side === 0 ? w * 0.7 : w * 0.3;
    d += ` C${x} ${g - 60}, ${lx - dir * 70} ${g - 6}, ${lx} ${g}`;
    d += ` C${lx + dir * 30} ${g + 4}, ${lx + dir * 30} ${g - 40}, ${lx + dir * 4} ${g - 40}`;
    d += ` C${lx - dir * 22} ${g - 40}, ${lx - dir * 16} ${g + 6}, ${lx + dir * 14} ${g + 8}`;
    d += ` C${lx + dir * 90} ${g + 10}, ${to} ${g + 40}, ${to} ${g + 150}`;
    x = to;
    y = g + 150;
    side = 1 - side;
  });

  const top = last - 170;
  if (top > y + 20) {
    d += ` C${x} ${y + (top - y) / 3}, ${x} ${y + ((top - y) * 2) / 3}, ${x} ${top}`;
  }
  d += ` C${x} ${last - 30}, ${w * 0.5} ${last - 110}, ${w * 0.5} ${last + 20}`;
  return d;
}

function InkThread() {
  const svgRef = useRef(null);
  const inkRef = useRef(null);
  const tipRef = useRef(null);
  const [shape, setShape] = useState(null);

  // Measure the page (and again whenever it changes size) and lay the line.
  useLayoutEffect(() => {
    const page = svgRef.current?.parentElement;
    if (!page) return undefined;
    const measure = () => {
      const box = page.getBoundingClientRect();
      const gaps = [...page.querySelectorAll('.hw-gap')].map((el) => {
        const r = el.getBoundingClientRect();
        return r.top - box.top + r.height / 2;
      });
      const d = buildPath(box.width, box.height, gaps);
      setShape((old) => (old?.d === d ? old : { w: box.width, h: box.height, d }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(page);
    return () => observer.disconnect();
  }, []);

  // Follow the scroll: the tip aims for a point a little below the middle of
  // the screen, and eases after it.
  useEffect(() => {
    const ink = inkRef.current;
    const tip = tipRef.current;
    if (!shape || !ink || !tip) return undefined;

    const total = ink.getTotalLength();
    ink.style.strokeDasharray = `${total} ${total}`;

    // Where on the line is the tip at a given height? The line loops back up
    // now and then, so go by the furthest it has got.
    const samples = [];
    let reach = -Infinity;
    for (let i = 0; i <= 400; i += 1) {
      const at = (total * i) / 400;
      reach = Math.max(reach, ink.getPointAtLength(at).y);
      samples.push([at, reach]);
    }
    const lengthAt = (y) => {
      const hit = samples.find(([, sy]) => sy >= y);
      return hit ? hit[0] : total;
    };

    let current = 0;
    let target = 0;
    let frame = null;

    // Leaving the page scrolls it back to the top (Layout.js), and that scroll
    // event can land after the page is gone but before this effect has been
    // cleaned up - the ref is already empty then.
    const aim = () => {
      if (!svgRef.current) return;
      const page = svgRef.current.parentElement.getBoundingClientRect();
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      target = atBottom ? total : lengthAt(window.innerHeight * 0.62 - page.top);
    };

    const draw = () => {
      if (!svgRef.current) return;
      current += (target - current) * 0.12;
      if (Math.abs(target - current) < 0.4) current = target;

      ink.style.strokeDashoffset = `${total - current}`;
      // The arrowhead sits on the end of the ink, pointing the way it goes.
      const p = ink.getPointAtLength(Math.max(3, current));
      const back = ink.getPointAtLength(Math.max(0, current - 3));
      const heading = (Math.atan2(p.y - back.y, p.x - back.x) * 180) / Math.PI;
      tip.setAttribute('transform', `translate(${p.x} ${p.y}) rotate(${heading})`);

      frame = current === target ? null : requestAnimationFrame(draw);
    };

    const onScroll = () => {
      if (!svgRef.current) return;
      aim();
      if (!frame) frame = requestAnimationFrame(draw);
    };

    if (reduceMotion()) {
      target = total;
      current = total;
      draw();
      return undefined;
    }

    aim();
    current = target;
    draw();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [shape]);

  return (
    <svg
      ref={svgRef}
      className="it"
      width={shape?.w ?? 0}
      height={shape?.h ?? 0}
      viewBox={shape ? `0 0 ${shape.w} ${shape.h}` : undefined}
      aria-hidden="true"
      focusable="false"
    >
      {shape && (
        <>
          <path className="it-guide" d={shape.d} />
          {/* One group, so where line and head overlap isn't darker. */}
          <g className="it-ink">
            <path ref={inkRef} d={shape.d} />
            <path ref={tipRef} d="M-12 -7 Q-5 -2.5 0 0 Q-5.5 3.2 -11 8" />
          </g>
        </>
      )}
    </svg>
  );
}

export default InkThread;
